import { api } from '@/api/client';
import type { Money } from '@/types/catalogue';

/**
 * Paying for a placed order — BUILD-PLAN §25, §26, §29.
 *
 * Authorised by the checkout token returned at placement, sent as a header.
 * NOT by the order number: those are sequential (`GLZ-2026-000021`), so
 * accepting one as proof would let anyone count upward and start payments
 * against other people's orders.
 */

export interface PaymentMethod {
  key: string;
  label: string;
  /**
   * What THIS gateway will charge, in the currency it settles.
   *
   * Different gateways settle in different money — Paystack takes naira for a
   * sterling order — so the amount belongs to the method, not to the panel. A
   * single figure shown above a list of methods would be wrong for most of
   * them (§56).
   */
  amount: Money;
  currency: string;
  converted: boolean;
}

export interface PaymentOptions {
  /** What the card will actually be charged in (§56). */
  currency: string;
  amount: Money;
  /** The order's own price, when it differs from the charge currency. */
  baseAmount: Money;
  converted: boolean;
  paymentStatus: string;
  /** Only gateways that are configured AND can settle this currency (§86). */
  methods: PaymentMethod[];
}

export interface PaymentAttempt {
  status: string;
  reference: string | null;
  /** Where to send the customer, when the gateway hosts the payment page. */
  redirectUrl: string | null;
  message: string;
}

export const paymentKeys = {
  options: (orderNumber: string) => ['payment', 'options', orderNumber] as const,
};

const withToken = (token: string) => ({ 'X-Checkout-Token': token });

export function fetchPaymentOptions(
  orderNumber: string,
  token: string,
): Promise<PaymentOptions> {
  return api.get<PaymentOptions>(
    `/checkout/orders/${encodeURIComponent(orderNumber)}/payment`,
    undefined,
    { headers: withToken(token) },
  );
}

export function startPayment(
  orderNumber: string,
  token: string,
  method: string,
  idempotencyKey: string,
): Promise<PaymentAttempt> {
  return api.post<PaymentAttempt>(
    `/checkout/orders/${encodeURIComponent(orderNumber)}/payment`,
    /*
     * The key is generated once per attempt and reused on retry, so a
     * double-click or a dropped connection resolves to one charge rather
     * than two (§26). It is never regenerated by a re-render.
     */
    { method, idempotency_key: idempotencyKey },
    { headers: withToken(token) },
  );
}

/**
 * Ask the server to settle by checking with the provider — §26, §27.
 *
 * Called when the customer returns from a hosted payment page. It does not
 * assert that payment succeeded; it asks the backend to find out, which the
 * backend does by calling the provider's own API. Safe to call repeatedly.
 */
export function verifyPayment(
  orderNumber: string,
  checkoutToken: string,
): Promise<{ paymentStatus: string; paid: boolean; message: string }> {
  return api.post(
    `/checkout/orders/${orderNumber}/payment/verify`,
    {},
    checkoutToken ? { headers: { 'X-Checkout-Token': checkoutToken } } : {},
  );
}
